import { ReactNode } from 'react';

import {
  Box,
  Divider,
  Icons,
  notify,
  PreviewSnippet,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  colorOrToken,
  tokens,
} from '@cast/design-system';

export type OperatingSystem = 'macos' | 'linux' | 'windows';

const OPERATING_SYSTEMS_TOGGLE_CONFIG: Record<
  OperatingSystem,
  { Icon: typeof Icons.AppleLogo; text: string; value: OperatingSystem }
> = {
  macos: { Icon: Icons.AppleLogo, text: 'MacOS', value: 'macos' },
  linux: { Icon: Icons.LinuxLogo, text: 'Linux', value: 'linux' },
  windows: { Icon: Icons.WindowsLogo, text: 'Windows', value: 'windows' },
};

const BASE_URL = 'https://get.cast.ai';

const INSTALL_CASTCTL_COMMANDS = {
  macos: [
    `brew tap castai/tap
brew trust --cask castai/tap/castctl
brew install castctl`,
    `curl ${BASE_URL}/macos | bash`,
  ],
  linux: `curl ${BASE_URL}/linux | bash`,
  windows: `iwr -useb ${BASE_URL}/windows | iex`,
} satisfies Record<OperatingSystem, string | string[]>;

const Step = ({ number }: { number: number }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      bgcolor: colorOrToken('blue.500', tokens.theme.bg.categorical.blueStrong),
      color: colorOrToken('white', tokens.theme.fg.structural.primaryOnInverse),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      typography: 'A2',
      flexShrink: 0,
    }}
  >
    {number}
  </Box>
);

type InstallStepRowProps = {
  number: number;
  isLast?: boolean;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
};

export const InstallStepRow = ({
  number,
  isLast,
  title,
  description,
  children,
}: InstallStepRowProps) => (
  <Stack direction="row" gap={16}>
    <Stack alignItems="center" width={24} flexShrink={0}>
      <Step number={number} />
      <Divider
        orientation="vertical"
        sx={{ flexGrow: 1, height: 'unset', my: 8 }}
      />
    </Stack>
    <Box flexGrow={1} pb={isLast ? 0 : 24}>
      <Stack gap={12}>
        <Stack gap={2}>
          <Typography variant="A1">{title}</Typography>
          {!!description && (
            <Typography
              variant="P12R"
              color={colorOrToken(
                'grey.400',
                tokens.theme.fg.structural.secondary
              )}
            >
              {description}
            </Typography>
          )}
        </Stack>
        {children}
      </Stack>
    </Box>
  </Stack>
);

export const InstallCastctlOperatingSystemToggle = ({
  value,
  onChange,
}: {
  value: OperatingSystem;
  onChange: (os: OperatingSystem) => void;
}) => (
  <ToggleButtonGroup
    size="sm"
    value={value}
    exclusive
    onChange={(_event, next: OperatingSystem | null) => {
      if (next) onChange(next);
    }}
    testId="os-toggle"
  >
    {Object.values(OPERATING_SYSTEMS_TOGGLE_CONFIG).map(
      ({ Icon, text, value: osValue }) => (
        <ToggleButton
          key={osValue}
          icon={<Icon weight="fill" />}
          value={osValue}
          text={text}
        />
      )
    )}
  </ToggleButtonGroup>
);

export const InstallCastctlCommand = ({
  operatingSystem,
}: {
  operatingSystem: OperatingSystem;
}) => {
  const handleCopy = () => {
    notify.success('Copied to clipboard');
  };

  if (operatingSystem === 'macos') {
    return (
      <Stack gap={4}>
        <PreviewSnippet
          code={INSTALL_CASTCTL_COMMANDS.macos[0]}
          onCopy={handleCopy}
          testId="install-snippet-macos-primary"
        />
        <Typography variant="P12R" color="text.secondary">
          Or
        </Typography>
        <PreviewSnippet
          code={INSTALL_CASTCTL_COMMANDS.macos[1]}
          onCopy={handleCopy}
          testId="install-snippet-macos-secondary"
        />
      </Stack>
    );
  }

  return (
    <PreviewSnippet
      code={INSTALL_CASTCTL_COMMANDS[operatingSystem]}
      onCopy={handleCopy}
      testId={`install-snippet-${operatingSystem}`}
    />
  );
};
